import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { wrapRequest } from 'helpers/requests';

import { media } from 'context';
import { Grid, Layout, Link, ProductImg, RequestState, Text } from 'ds';
import { submitFeedback } from 'ds/FeedbackRequests/requests';
import { PANTRY_ORDERS_PATH } from 'routes';
import { actions as dashboardActions } from 'store/Dashboard';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { selectLoggedInUserId } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';

import FeedbackSubwidget from './FeedbackSubwidget';
import OrderProgressBar from './OrderProgressBar';
import { getDashboardWidgetConfigs } from './requests';
import { ConsumablesOrderWidgetData, WidgetVariant } from './types';
import { formatDeliveryDate, getConsumablesOrderProgressPercent, getRelativeDateWord } from './utils';

interface Props {
  data: ConsumablesOrderWidgetData;
  variant: WidgetVariant;
}

let animateProgressBar = true;

const ConsumablesOrderWidget: React.FC<Props> = ({
  data: { id, delivery_date, items, status, task_id, feedback },
  variant = 'full'
}) => {
  const { isMobile } = useContext(media);
  const userId = useSelector(selectLoggedInUserId);
  const tenancyId = useSelector(selectTenancyId);
  const progressPercent = getConsumablesOrderProgressPercent(status);
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const deliveryText = {
    open: 'Order open',
    locked: 'Order processing',
    delivered: formatDeliveryDate({ date: delivery_date, format: { month: 'short', day: 'numeric' }, status })
  }[status];
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const dispatch = useAppDispatch();
  useEffect(() => {
    setTimeout(() => {
      animateProgressBar = false;
    }, 1000);
  }, []);
  const handleFeedbackSubmit = async (liked: boolean) => {
    if (!task_id || !tenancyId || !widgetConfigs) return;

    setRequestState('in_progress');
    await wrapRequest(
      submitFeedback({
        userId,
        feedback: { liked, task_id }
      }),
      { successMessage: 'Feedback submitted.' }
    );

    const updatedWidgetConfigs = widgetConfigs.map(row =>
      row.map(config => {
        if (config.type === 'consumables_order' && config.data?.id === id) {
          return {
            ...config,
            data: {
              ...config.data,
              feedback: { liked }
            }
          };
        }
        return config;
      })
    );

    dispatch(dashboardActions.setWidgetConfigs(updatedWidgetConfigs));
    setTimeout(async () => {
      const widgetConfigs = await getDashboardWidgetConfigs({ tenancyId });
      dispatch(dashboardActions.setWidgetConfigs(widgetConfigs));
    }, 1000);
  };

  if (variant === 'half') {
    const imageWidth = 22;
    return (
      <Link href={`${PANTRY_ORDERS_PATH}/${id}`} color="black" fullWidth fullHeight>
        <Layout borderRadius={16} color="gray-25" padding={12} align="center" gap={16} height="100%">
          <Layout width={imageWidth * 2 + 4 + 4 * 2} padding={4} color="white" borderRadius={8}>
            <Grid width={22} gap={4} itemsPerRow={isMobile ? 2 : undefined}>
              {items.slice(0, 2).map(item => (
                <ProductImg
                  key={item.id}
                  srcKey={item.image.key}
                  size="thumbnail"
                  alt={item.name}
                  style={{ width: 22, height: 22 }}
                />
              ))}
              {items.slice(2, 4).map((item, mapIndex) => {
                const index = mapIndex + 2;

                return (
                  <React.Fragment key={item.id}>
                    {index === 3 && items.length > 4 ? (
                      <Layout
                        width={imageWidth}
                        height={imageWidth}
                        align="center"
                        justify="center"
                        borderRadius={4}
                        color="gray-25"
                        display="inline-flex"
                      >
                        <Text size="body-xs" color="gray-700">
                          +{items.length - 4}
                        </Text>
                      </Layout>
                    ) : (
                      <ProductImg
                        srcKey={item.image.key}
                        size="thumbnail"
                        alt={item.name}
                        style={{ width: 22, height: 22 }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Layout>
          <Layout direction="column" gap={4} flexGrow={1}>
            <Text size="body-xs">{isMobile ? 'Restocking' : 'Restocking order'}</Text>
            {!isMobile && <OrderProgressBar progressPercent={progressPercent} animate={animateProgressBar} />}
            <Text size="body-xs" color="gray-400">
              {deliveryText}
            </Text>
          </Layout>
        </Layout>
      </Link>
    );
  }

  return (
    <Link href={`${PANTRY_ORDERS_PATH}/${id}`} color="black" search={`?id=${id}`} fullWidth fullHeight>
      <Layout borderRadius={16} direction="column" color="gray-25" padding={12} height="100%" gap={4}>
        <Layout borderRadius={8} justify="space-between" color="white" paddingY={10} paddingX={12}>
          {items.slice(0, 2).map(item => (
            <ProductImg
              key={item.id}
              srcKey={item.image.key}
              size="thumbnail"
              alt={item.name}
              style={{ width: 64, height: 64 }}
            />
          ))}
          {items.length > 3 ? (
            <Layout align="center" justify="center" borderRadius={8} color="gray-25" width={64} height={64}>
              <Text size="body-md" color="gray-700" semibold>
                +{items.length - 2}
              </Text>
            </Layout>
          ) : items[2] ? (
            <ProductImg
              srcKey={items[2].image.key}
              size="thumbnail"
              alt={items[2].name}
              style={{ width: 64, height: 64 }}
            />
          ) : null}
        </Layout>
        {status === 'delivered' && task_id ? (
          <Layout marginTop={4}>
            <FeedbackSubwidget
              disabled={requestState === 'in_progress'}
              text={`${items.length} items restocked ${getRelativeDateWord(delivery_date)}`}
              handleFeedback={handleFeedbackSubmit}
              liked={feedback?.liked}
              includeModal={false}
            />
          </Layout>
        ) : (
          <Layout direction="column" gap={4} flexGrow={1} paddingX={4} padding={6}>
            <Text size="body-xs">Restocking order</Text>
            <OrderProgressBar progressPercent={progressPercent} animate={animateProgressBar} />
            <Text size="body-xs" color="gray-400">
              {deliveryText}
            </Text>
          </Layout>
        )}
      </Layout>
    </Link>
  );
};

export default ConsumablesOrderWidget;
